import {
  calendarDateRange,
  calendarRangeLabel,
} from "@/features/ai-chat/calendarPageContext";
import { useAiChatPageContext } from "@/features/ai-chat/useAiChatPageContext";
import { api } from "@/lib/api-client";
import { calculateYOffsetForTime } from "@/Utilities";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import styled from "styled-components";
import Calendar from "./components/calendar/Calendar";
import CalendarModalWrapper from "./components/calendar/CalendarModalWrapper";
import { timeOffFetchWindow } from "./components/calendar/monthTimeOffs";
import { useCalendarStore } from "./store/useCalendarStore";
import Header from "./components/Header";
import { useShallow } from "zustand/react/shallow";
import { useSearchParams } from "react-router";
import { useTheme } from "next-themes";
import { LazyCalendarOnboardingTour } from "@/features/onboarding-tour/LazyCalendarOnboardingTour";

const CalendarPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get("date");
  const locationIdParam = searchParams.get("locationId");
  const scrollApptIdParam = searchParams.get("scrollApptId");
  const scrollTimeParam = searchParams.get("scrollTime");

  const { theme, resolvedTheme } = useTheme();
  const activeTheme = theme === "system" ? resolvedTheme : theme;

  const {
    calendarData,
    dateActions,
    locationActions,
    refreshCalendar,
    selectedLocationId,
    locations,
    setTimeoffs,
    calendarView,
    selectedDate,
  } = useCalendarStore(
    useShallow((state) => ({
      calendarData: state.calendarData,
      dateActions: state.dateActions,
      locationActions: state.locationActions,
      refreshCalendar: state.refreshCalendar,
      selectedLocationId: state.selectedLocationId,
      locations: state.locations,
      setTimeoffs: state.setTimeoffs,
      calendarView: state.calendarView,
      selectedDate: state.selectedDate,
    })),
  );

  // `selectedLocationId` is the location the calendar data was fetched with.
  // `filterItems.FK_locationId` is what the dropdown shows and the two can
  // diverge mid-switch (DEV-8678), so the assistant is told about the data on
  // screen, never about the pending selection.
  useAiChatPageContext(
    selectedLocationId == null || calendarView == null
      ? null
      : {
          payload: {
            page: "calendar",
            locationId: selectedLocationId,
            view: calendarView,
            ...calendarDateRange(dayjs(selectedDate), calendarView),
          },
          label: [
            locations?.find(
              (location: { locationId: number }) =>
                location.locationId === selectedLocationId,
            )?.name,
            calendarRangeLabel(dayjs(selectedDate), calendarView),
          ]
            .filter(Boolean)
            .join(" · "),
        },
  );

  const [isMonthCalendar, setIsMonthCalendar] = React.useState(false);
  const [highlightedDate, setHighlightedDate] = React.useState<string | null>(
    null,
  );

  const {
    data: timeoffs,
    refetch: refetchTimeoffs,
    isLoading: isFetchingTimeoffs,
  } = api.timeoff.useGetTimeoff(
    {
      locationId: selectedLocationId,
      ...timeOffFetchWindow(dayjs(selectedDate), calendarView),
      includeResources: true,
    },
    selectedLocationId != null,
  );

  useEffect(() => {
    setTimeoffs(timeoffs);
  }, [timeoffs]);

  useEffect(() => {
    if (dateParam) {
      dateActions.setSelectedDate(dayjs(dateParam).locale("sl"));
      setHighlightedDate(dateParam);

      // Clear highlight after 2 seconds
      const timeout = setTimeout(() => {
        setHighlightedDate(null);
      }, 2000);

      setSearchParams(
        (prev) => {
          prev.delete("date");
          return prev;
        },
        { replace: true },
      );

      return () => clearTimeout(timeout);
    }
  }, [dateParam]);

  useEffect(() => {
    if (!locationIdParam) {
      return;
    }

    const parsedLocationId = Number(locationIdParam);
    const isValidLocationId =
      Number.isInteger(parsedLocationId) && parsedLocationId > 0;

    if (isValidLocationId && selectedLocationId !== parsedLocationId) {
      locationActions.setSelectedLocationId(parsedLocationId);
      refreshCalendar(true).catch(console.log);
    }

    setSearchParams(
      (prev) => {
        prev.delete("locationId");
        return prev;
      },
      { replace: true },
    );
  }, [
    locationIdParam,
    locationActions,
    refreshCalendar,
    selectedLocationId,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!scrollApptIdParam && !scrollTimeParam) {
      return;
    }

    // scrollApptId is user-controllable (URL param); only use it when it is a
    // valid positive integer so it can't throw inside querySelector or be used
    // to inject an arbitrary selector.
    const parsedApptId = scrollApptIdParam ? Number(scrollApptIdParam) : null;
    const validScrollApptId =
      parsedApptId !== null &&
      Number.isInteger(parsedApptId) &&
      parsedApptId > 0
        ? parsedApptId
        : null;

    let attemptCount = 0;
    const maxAttempts = 40;

    function clearScrollParams() {
      setSearchParams(
        (prev) => {
          prev.delete("scrollApptId");
          prev.delete("scrollTime");
          return prev;
        },
        { replace: true },
      );
    }

    const selectedLocationTimezone = locations.find(
      (location: { locationId: number; timeZone?: string }) =>
        location.locationId === selectedLocationId,
    )?.timeZone;

    function scrollToTarget() {
      const calendarRow = document.getElementById("calendar_row");
      if (!calendarRow) {
        return false;
      }

      if (validScrollApptId !== null) {
        const appointmentElement = document.querySelector(
          `[data-appointment-id="${validScrollApptId}"]`,
        );

        if (appointmentElement instanceof HTMLElement) {
          appointmentElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
          return true;
        }
      }

      if (scrollTimeParam) {
        const yOffset = calculateYOffsetForTime({
          time: scrollTimeParam,
          startTime: calendarData.startTime,
          endTime: calendarData.endTime,
          timeStep: calendarData.timeStep,
          slotHeight: calendarData.slotHeight,
          date: dayjs(selectedDate).format("YYYY-MM-DD"),
          timeZone: selectedLocationTimezone,
        });

        const rowTop = calendarRow.getBoundingClientRect().top + window.scrollY;
        const headerHeight = parseFloat(
          getComputedStyle(calendarRow).getPropertyValue(
            "--calendar-header-height",
          ),
        );
        const stickyOffset = 120 + headerHeight;
        window.scrollTo({
          top: Math.max(rowTop + yOffset - stickyOffset, 0),
          behavior: "smooth",
        });
        return true;
      }

      return false;
    }

    const animationFrameId = requestAnimationFrame(() => {
      if (scrollToTarget()) {
        clearScrollParams();
      }
    });

    const intervalId = setInterval(() => {
      attemptCount += 1;
      if (scrollToTarget() || attemptCount >= maxAttempts) {
        clearInterval(intervalId);
        clearScrollParams();
      }
    }, 100);

    return () => {
      cancelAnimationFrame(animationFrameId);
      clearInterval(intervalId);
    };
  }, [
    calendarData.endTime,
    calendarData.slotHeight,
    calendarData.startTime,
    calendarData.timeStep,
    locations,
    scrollApptIdParam,
    scrollTimeParam,
    selectedDate,
    selectedLocationId,
    setSearchParams,
  ]);

  useEffect(() => {
    if (window.innerWidth < 768) {
      dateActions.setCalendarView("day");
    } else {
      refreshCalendar().catch(console.log);
    }

    const refreshCalendarInterval = setInterval(
      () => {
        refreshCalendar(true).catch(console.log);
      },
      5 * 60 * 1000,
    );

    return () => {
      clearInterval(refreshCalendarInterval);
    };
  }, []);

  return (
    <>
      <Wrapper
        isMonthCalendar={isMonthCalendar}
        isDark={activeTheme === "dark"}
        className="dark:bg-neutral-900"
      >
        <Header />

        <Calendar
          setIsMonthCalendar={setIsMonthCalendar}
          isFetchingTimeoffs={isFetchingTimeoffs}
          highlightedDate={highlightedDate}
        ></Calendar>
      </Wrapper>

      <CalendarModalWrapper refetchTimeoffs={refetchTimeoffs} />

      <LazyCalendarOnboardingTour />
    </>
  );
};

const Wrapper = styled.div<{ isMonthCalendar: boolean; isDark: boolean }>`
  padding-bottom: 100px;
  position: relative;
  height: fit-content;

  @media (min-width: 768px) {
    padding: 5px 10px;
  }

  display: grid;

  grid-template-columns: ${({ isMonthCalendar }) =>
    isMonthCalendar ? "" : "auto 1fr"};

  grid-template-rows: auto;
  grid-auto-rows: minmax(100px, auto);
  width: 100%;

  --calendar-header-height: 0px;

  @media (min-width: 769px) {
    --calendar-header-height: 55px;
  }

  .top_row {
    position: sticky;
    /* Pin below the status-bar strip (body::before in index.css) so the
       day/employee row isn't hidden behind the notch when scrolled. */
    top: calc(var(--safe-area-top, 0px) + var(--calendar-header-height));

    background: ${({ isDark }) => (isDark ? "#121212" : "white")};
    z-index: 5;
  }

  .times_row {
    position: sticky;
    left: 0;

    background: ${({ isDark }) => (isDark ? "#121212" : "white")};
    z-index: 3;

    /*  border-right: 1px solid #eaebed; */
    width: 45px;
    text-align: right;
  }

  .scroll_row {
    overflow-x: scroll;
    overflow-y: hidden;
  }

  .empty {
    z-index: 4;

    left: 0;
  }

  .calendar_row {
    z-index: 1;
  }

  #users_row {
    ::-webkit-scrollbar {
      display: none;
    }
  }

  #calendar_row {
    border: 1px solid ${({ isDark }) => (isDark ? "#121212" : "#d8d8d8")};
    border-top: none;
  }
`;

export default CalendarPage;
